import { useState, useCallback, useMemo } from 'react';

export function useSortBy<const T extends readonly string[]>(
    sortByOptions: T,
    initialOption?: T[number]
) {
    if (!sortByOptions || sortByOptions.length === 0) {
        throw new Error('useSortBy: sortByOptions cannot be empty');
    }

    const getInitialIndex = useCallback(() => {
        if (initialOption) {
            const index = sortByOptions.indexOf(initialOption);
            if (index === -1) {
                console.warn(`useSortBy: initialOption "${initialOption}" not found in options`);
                return 0;
            }
            return index;
        }
        return 0;
    }, [initialOption, sortByOptions]);

    const [sortIndex, setSortIndex] = useState(getInitialIndex);

    const currentOption: T[number] = useMemo(
        () => sortByOptions[sortIndex],
        [sortByOptions, sortIndex]
    );

    const nextOption = useCallback(() => {
        setSortIndex((prevIndex) => (prevIndex + 1) % sortByOptions.length);
    }, [sortByOptions.length]);

    const prevOption = useCallback(() => {
        setSortIndex((prevIndex) =>
            (prevIndex - 1 + sortByOptions.length) % sortByOptions.length
        );
    }, [sortByOptions.length]);

    const sortByOption = useCallback((option: T[number]) => {
        const index = sortByOptions.indexOf(option);
        if (index !== -1) {
            setSortIndex(index);
        } else {
            console.warn(`useSortBy: option "${option}" not found in available options`);
        }
    }, [sortByOptions]);

    const reset = useCallback(() => {
        setSortIndex(getInitialIndex());
    }, [getInitialIndex]);

    const isCurrentOption = useCallback((option: T[number]) => {
        return currentOption === option;
    }, [currentOption]);

    return {
        currentOption,
        nextOption,
        prevOption, 
        sortByOption,
        reset,
        isCurrentOption,
        options: sortByOptions,
        currentIndex: sortIndex,
    } as const;
}